import React, { useEffect } from "react";
import Modal from "@cloudscape-design/components/modal";
import FormField from "@cloudscape-design/components/form-field";
import Toggle from "@cloudscape-design/components/toggle";
import Multiselect from "@cloudscape-design/components/multiselect";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { tgBotsRotationStore } from "../stores/tgBotsRotation";

const formatDateTime = (value) => {
	if (!value) return "-";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;

	return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

export default function TgBotsRotationModal({ Interface }) {
	const rotation = useRecoilValue(tgBotsRotationStore);
	const setRotation = useSetRecoilState(tgBotsRotationStore);
	const patch = (values) => setRotation((current) => ({ ...current, ...values }));
	const close = () => patch({ show: false });

	useEffect(() => {
		Interface.loadTgBotsRotation();
	}, []);

	const options = (rotation.eligibleBots || []).map((bot) => ({
		value: String(bot.id),
		label: `#${bot.id} — ${bot.bot_link || "bot"} (${bot.status})`,
	}));
	const selectedOptions = (rotation.botIds || [])
		.map((id) => options.find((option) => option.value === String(id)))
		.filter(Boolean);
	const errorText = typeof rotation.error === "string" ? rotation.error : rotation.error?.text || "";

	const save = () => {
		const intervalDays = Number(rotation.intervalDays);

		if (rotation.intervalDays === "" || !Number.isInteger(intervalDays) || intervalDays < 1) {
			Interface.addNoty("Error", "Interval must be a positive number of days");
			return;
		}

		if (rotation.enabled && rotation.botIds.length < 2) {
			Interface.addNoty("Error", "Select at least two actual bots to enable rotation");
			return;
		}

		Interface.saveTgBotsRotation({
			enabled: rotation.enabled,
			botIds: rotation.botIds,
			intervalDays,
		})
			.then(() => {
				Interface.addNoty("Success", "Telegram bots rotation was saved");
				patch({ show: false, saving: false });
			})
			.catch((error) => {
				Interface.addNoty("Error", error?.text || error?.toString?.() || "Request failed");
			});
	};

	return (
		<Modal
			visible={rotation.show}
			size="medium"
			header="Telegram bots auto rotation"
			onDismiss={close}
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="s">
						<Button onClick={close}>Close</Button>
						<Button variant="primary" loading={rotation.saving} disabled={!rotation.loaded} onClick={save}>
							Save
						</Button>
					</SpaceBetween>
				</Box>
			}
		>
			{rotation.loaded ? (
				<SpaceBetween direction="vertical" size="m">
					{errorText ? (
						<Alert type="error" header="Error loading rotation">
							{errorText}
						</Alert>
					) : null}
					<FormField label="Enabled">
						<Toggle checked={rotation.enabled} onChange={({ detail }) => patch({ enabled: detail.checked })}>
							{rotation.enabled ? "On" : "Off"}
						</Toggle>
					</FormField>
					<FormField label="Bots to rotate" description="Only bots with status current or free are available." stretch>
						<Multiselect
							filteringType="auto"
							selectedOptions={selectedOptions}
							options={options}
							placeholder="Select bots"
							onChange={({ detail }) =>
								patch({
									botIds: detail.selectedOptions.map((option) => Number(option.value)),
								})
							}
						/>
					</FormField>
					<FormField label="Rotation interval (days)" stretch>
						<Input
							type="number"
							inputMode="numeric"
							placeholder="14"
							value={rotation.intervalDays}
							onChange={({ detail }) => patch({ intervalDays: detail.value })}
						/>
					</FormField>
					<FormField label="Next rotation">
						<Box>{formatDateTime(rotation.nextRotationAt)}</Box>
					</FormField>
				</SpaceBetween>
			) : (
				<Box textAlign="center">
					<Spinner size="large" />
				</Box>
			)}
		</Modal>
	);
}
